/**
 * Platform Admin Layout
 *
 * Platform-level administration (formerly "Super Admin"):
 * - Warning banner for platform control mode
 * - Sidebar navigation for DB-backed sections only
 * - Impersonation indicator
 */

import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Menu,
  Building2,
  Users,
  ShieldCheck,
  ChevronLeft,
  AlertTriangle,
  UserCheck,
  XCircle,
} from 'lucide-react';
import {
  isImpersonating,
  getImpersonationSession,
  endImpersonation,
  isSignedInPersonPlatformAdmin,
} from '../../lib/superAdminService';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useMobileExperience } from '../../mobile/featureFlag';
import { BottomSheet } from '../../mobile/BottomSheet';

// ============================================================================
// Navigation Config (only items that exist and are DB-backed)
// ============================================================================

const navigation = [
  { name: 'Dashboard', href: '/platform-admin', icon: LayoutDashboard, description: 'Platform overview & health' },
  { name: 'Tenants', href: '/platform-admin/tenants', icon: Building2, description: 'List all tenants (organizations)' },
  { name: 'Workspaces', href: '/platform-admin/workspaces', icon: Building2, description: 'Manage all workspaces' },
  { name: 'Create User', href: '/platform-admin/users/new', icon: Users, description: 'Create user with email and password' },
  { name: 'Admin Roles', href: '/platform-admin/admin-roles', icon: ShieldCheck, description: 'Platform, tenant & workspace admins' },
];

// ============================================================================
// Components
// ============================================================================

function ImpersonationBanner() {
  const session = getImpersonationSession();
  
  if (!session) return null;
  
  return (
    <div className="bg-[var(--mustard)] text-[var(--mustard)] px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCheck className="h-5 w-5" />
          <span className="font-medium">
            Impersonating user: {session.impersonatedUserId}
          </span>
          <span className="text-[var(--mustard)] text-sm">
            Reason: {session.reason}
          </span>
        </div>
        <button
          onClick={() => {
            endImpersonation();
            window.location.reload();
          }}
          className="flex items-center gap-1 px-3 py-1 bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white text-sm font-medium transition-colors"
        >
          <XCircle className="h-4 w-4" />
          End Impersonation
        </button>
      </div>
    </div>
  );
}

/**
 * The site's links. `tone` picks the ground they sit on: the dark desktop
 * sidebar, or the phone's sheet (light or dark with the app theme).
 */
function NavLinks({ tone, onNavigate }: { tone: 'sidebar' | 'sheet'; onNavigate?: () => void }) {
  const idle =
    tone === 'sidebar'
      ? 'text-[var(--text-ter)] hover:bg-[var(--bg-raised)] hover:text-white'
      : 'text-[var(--text-sec)] active:bg-[var(--hover-tint)]';
  return (
    <>
      {navigation.map(item => (
        <NavLink
          key={item.href}
          to={item.href}
          end={item.href === '/platform-admin'}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm transition-colors
             ${isActive ? 'bg-[var(--coral)] text-white' : idle}`
          }
        >
          <item.icon className="h-5 w-5 flex-shrink-0" />
          <div>
            <div className="font-medium">{item.name}</div>
            <div className="text-xs opacity-70">{item.description}</div>
          </div>
        </NavLink>
      ))}
    </>
  );
}

function Sidebar() {
  return (
    <aside className="w-64 bg-[var(--bg)] text-white flex flex-col h-screen sticky top-0">
      {/* Header */}
      <div className="p-4 border-b border-[var(--rule)]">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[var(--coral)]">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-bold text-lg">Platform Admin</h1>
            <p className="text-xs text-[var(--text-ter)]">Platform Control</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <NavLinks tone="sidebar" />
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--rule)]">
        <NavLink
          to="/prioritization"
          className="flex items-center gap-2 text-sm text-[var(--text-ter)] hover:text-white transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to App
        </NavLink>
      </div>
    </aside>
  );
}

/**
 * The phone's version of the sidebar (audit 2026-11-02, #41): at 375 px the
 * fixed 256 px sidebar left about 120 px for the page. A bar names the site
 * and the page; its menu button opens the same links in the phone frame's
 * bottom sheet.
 */
function PhoneBar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const current =
    navigation
      .filter((item) => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0]?.name ?? 'Platform Admin';
  return (
    <>
      <div className="sticky top-0 z-30 flex h-12 items-center gap-2 bg-[var(--bg)] px-2 text-white border-b border-[var(--rule)]">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Platform Admin menu"
          aria-expanded={open}
          className="inline-flex h-10 w-10 items-center justify-center text-[var(--text-ter)] active:bg-[var(--bg-raised)]"
        >
          <Menu className="h-5 w-5" />
        </button>
        <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[var(--coral)]" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{current}</span>
      </div>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="Platform Admin">
        <nav className="space-y-1">
          <NavLinks tone="sheet" onNavigate={() => setOpen(false)} />
          <NavLink
            to="/prioritization"
            onClick={() => setOpen(false)}
            className="mt-3 flex min-h-[44px] items-center gap-2 px-3 text-sm text-[var(--text-sec)] active:bg-[var(--hover-tint)]"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to App
          </NavLink>
        </nav>
      </BottomSheet>
    </>
  );
}

// ============================================================================
// Main Layout
// ============================================================================

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const userId = useUserStore(state => state.userId);
  const isPhone = useMobileExperience();

  // The server says who may enter. The role in the browser's stored user state
  // is not consulted: it can be stale, and it is the browser's to change. Asked
  // again if the signed-in person changes.
  const [access, setAccess] = useState<'asking' | 'allowed' | 'refused'>('asking');
  useEffect(() => {
    let current = true;
    setAccess('asking');
    isSignedInPersonPlatformAdmin()
      .then((isAdmin) => { if (current) setAccess(isAdmin ? 'allowed' : 'refused'); })
      .catch(() => { if (current) setAccess('refused'); });
    return () => { current = false; };
  }, [userId]);

  // Nothing of the site is drawn, and no page inside it mounts, until it answers.
  if (access === 'asking') {
    return <div className="min-h-screen bg-[var(--bg)]" data-testid="platform-admin-asking" aria-busy="true" />;
  }

  // Access denied
  if (access === 'refused') {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[var(--bg-raised)] p-8 text-center">
          <div className="p-4 bg-[var(--coral)]/20 rounded-full w-fit mx-auto mb-6">
            <AlertTriangle className="h-12 w-12 text-[var(--coral)]" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Access Denied</h1>
          <p className="text-[var(--text-ter)] mb-6">
            You don't have super admin privileges. This area is restricted to platform administrators.
          </p>
          <button
            onClick={() => navigate('/prioritization')}
            className="px-6 py-3 border border-[var(--rule)] hover:bg-[var(--surface)] text-[var(--text-pri)] font-medium transition-colors"
          >
            Return to Application
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[var(--hover-tint)]">
      {/* Warning Banner */}
      <div className="bg-[var(--coral)] text-white px-4 py-2 text-center text-sm font-medium">
        <AlertTriangle className="inline h-4 w-4 mr-2" />
        PLATFORM ADMIN - Changes here affect the entire platform
      </div>
      
      {/* Impersonation Banner */}
      {isImpersonating() && <ImpersonationBanner />}
      
      {/* Main Layout — on a phone the sidebar becomes a bar and a sheet */}
      {isPhone ? (
        <>
          <PhoneBar />
          <main className="min-h-screen min-w-0 overflow-x-hidden">
            <Outlet />
          </main>
        </>
      ) : (
        <div className="flex">
          <Sidebar />

          <main className="flex-1 min-h-screen">
            <Outlet />
          </main>
        </div>
      )}
    </div>
  );
}
